import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router';
import {
  ROI_CALCULATORS,
  ROI_CALCULATOR_IDS,
  ROI_LINK_KIND_LABELS,
  computeRoi,
  isRoiCalculator,
  roiDisplay,
  hasPayback,
  type RoiProject,
} from '@wr/shared';
import { ApiError, errorMessage, get } from '../../api.js';
import { Icon } from '../../components/Icon.js';
import { EmptyState, Notice, PageHead } from '../../components/ui.js';
import { useSignedIn } from '../../session.js';

interface Page {
  items: RoiProject[];
  total: number;
  page: number;
  pageSize: number;
}

/** The prototype's date: 28 Oct 2026. */
export function formatDay(value: string): string {
  return new Date(value).toLocaleDateString('en-CA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** EN, USD or FR, EUR (the list's tag of a calculator). */
const calculatorTag = (id: string) =>
  isRoiCalculator(id)
    ? `${ROI_CALCULATORS[id].lang.toUpperCase()}, ${ROI_CALCULATORS[id].currency}`
    : id;

const calculatorShort = (id: string) =>
  isRoiCalculator(id)
    ? `C&D ${ROI_CALCULATORS[id].lang.toUpperCase()} (${ROI_CALCULATORS[id].currency})`
    : id;

/** Payback of a row: "1.67 yr" or "No payback", computed on the device from its inputs. */
function paybackOf(project: RoiProject): string {
  if (!isRoiCalculator(project.calculator)) return '';
  const result = computeRoi(project.inputs);
  if (!hasPayback(result)) return 'No payback';
  return `${roiDisplay(result, project.calculator).text.payback} yr`;
}

/**
 * ROI simulator: the list of projects (brief section 7.7). Flat, newest
 * first; search on the client, the scenario and the linked record; filters
 * by calculator, author and period; pages of 20. No Deleted items tab:
 * restoring is an admin tool in Settings.
 */
export function RoiList() {
  const { user, refresh } = useSignedIn();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const calculator = params.get('calculator') ?? '';
  const author = params.get('author') === 'mine' ? 'mine' : 'all';
  const period = params.get('period') ?? 'all';
  const page = Number(params.get('page') ?? '1') || 1;
  const [search, setSearch] = useState(q);
  const [data, setData] = useState<Page | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const query = new URLSearchParams({ page: String(page), author, period });
    if (q) query.set('q', q);
    if (calculator) query.set('calculator', calculator);
    let current = true;
    get<Page>(`/roi/projects?${query.toString()}`)
      .then((res) => {
        if (!current) return;
        setData(res);
        setError(null);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) void refresh();
        else if (current) setError(errorMessage(err));
      });
    return () => {
      current = false;
    };
    // location.key: back from a project or a window, the list is read again.
  }, [q, calculator, author, period, page, refresh, location.key]);

  // Search as the person types, without a history step per letter.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (search.trim() === q) return;
      const next = new URLSearchParams(params);
      if (search.trim()) next.set('q', search.trim());
      else next.delete('q');
      next.delete('page');
      setParams(next, { replace: true });
    }, 300);
    return () => clearTimeout(timer);
  }, [search, q, params, setParams]);

  const set = (key: string, value: string, empty = '') => {
    const next = new URLSearchParams(params);
    if (value && value !== empty) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setParams(next);
  };
  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <>
      <PageHead
        title="ROI simulator"
        actions={
          <Link className="btn sm primary" to={{ pathname: '/roi/new', search: location.search }}>
            + New Project
          </Link>
        }
      />
      <div className="toolbar">
        <input
          className="grow"
          type="search"
          placeholder="Search client, scenario, linked record"
          aria-label="Search projects"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          aria-label="Calculator"
          value={calculator}
          onChange={(e) => set('calculator', e.target.value)}
        >
          <option value="">All calculators</option>
          {ROI_CALCULATOR_IDS.map((id) => (
            <option key={id} value={id}>
              {calculatorShort(id)}
            </option>
          ))}
        </select>
        <select
          aria-label="Author"
          value={author}
          onChange={(e) => set('author', e.target.value, 'all')}
        >
          <option value="all">Everyone</option>
          <option value="mine">Only mine</option>
        </select>
        <select
          aria-label="Period"
          value={period}
          onChange={(e) => set('period', e.target.value, 'all')}
        >
          <option value="all">Any date</option>
          <option value="30">Last 30 days</option>
          <option value="year">This year</option>
        </select>
      </div>
      {error && <Notice kind="error">{error}</Notice>}
      {data && (
        <p className="note roi-count">
          {data.total} project{data.total === 1 ? '' : 's'}
          {data.total > data.pageSize ? `, page ${page} of ${pages}` : ''}
        </p>
      )}
      {data &&
        (data.items.length === 0 ? (
          <EmptyState title="No project found.">Change the search or the filters.</EmptyState>
        ) : (
          <ul className="list" aria-label="ROI projects">
            {data.items.map((project) => (
              <li key={project.id}>
                <button
                  type="button"
                  className="row"
                  onClick={() => navigate(`/roi/${project.id}`)}
                >
                  <span className="av lead" aria-hidden="true">
                    <Icon name="roi" />
                  </span>
                  <span className="tx">
                    <span className="t1">{project.link.company}</span>
                    <span className="t2">{project.scenarioName}</span>
                    <span className="le-tags">
                      <span className="tag">{calculatorTag(project.calculator)}</span>
                      <span className="tag">
                        {project.link.label} ({ROI_LINK_KIND_LABELS[project.link.kind]})
                      </span>
                      <span className="tag">
                        {project.createdBy.id === user.id
                          ? 'You'
                          : (project.createdBy.name ?? 'A former user')}
                      </span>
                    </span>
                  </span>
                  <span className="rt">
                    <span>{paybackOf(project)}</span>
                    <span>{formatDay(project.updatedAt)}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ))}
      {data && pages > 1 && (
        <nav className="pager" aria-label="Pages">
          <button
            type="button"
            className="btn sm"
            disabled={page <= 1}
            onClick={() => set('page', String(page - 1))}
          >
            Previous
          </button>
          <span>
            {page} / {pages}
          </span>
          <button
            type="button"
            className="btn sm"
            disabled={page >= pages}
            onClick={() => set('page', String(page + 1))}
          >
            Next
          </button>
        </nav>
      )}
      <Outlet />
    </>
  );
}
